import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useLanguage } from './language-context';
import { motion } from '../theme/tokens';

// CB-17 toast with undo (admin design `A:6454`: navy pill, bottom 84 px, centred,
// moRise 200 ms, z 220, max-width min(92vw, 520px)). One toast at a time — a new
// one replaces the current (the design never stacks). An undo toast stays longer
// and runs `onUndo` once when pressed. Announced politely (role="status").

export type ToastTone = 'neutral' | 'ok' | 'danger';

export interface ToastInput {
  message: string;
  tone?: ToastTone;
  /** Shows an Undo button; the callback runs at most once. */
  onUndo?: () => void;
  undoLabel?: string;
  /** ms; default 4000, 6000 with undo. */
  duration?: number;
}

interface ToastState extends ToastInput {
  id: number;
}

interface ToastContextValue {
  show: (toast: ToastInput) => void;
  dismiss: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const TOAST_MS = 4000;
export const TOAST_UNDO_MS = 6000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const seq = useRef(0);

  const dismiss = useCallback(() => setToast(null), []);
  const show = useCallback((input: ToastInput) => {
    seq.current += 1;
    setToast({ ...input, id: seq.current });
  }, []);

  useEffect(() => {
    if (!toast) return;
    const ms = toast.duration ?? (toast.onUndo ? TOAST_UNDO_MS : TOAST_MS);
    const h = setTimeout(() => setToast((cur) => (cur?.id === toast.id ? null : cur)), ms);
    return () => clearTimeout(h);
  }, [toast]);

  const value = useMemo(() => ({ show, dismiss }), [show, dismiss]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastView toast={toast} onDone={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastView({ toast, onDone }: { toast: ToastState | null; onDone: () => void }) {
  const { t } = useLanguage();
  const used = useRef<number | null>(null);
  return (
    <div className="toast-region" role="status" aria-live="polite">
      {toast ? (
        <div
          key={toast.id}
          className={`toast toast--${toast.tone ?? 'neutral'}`}
          style={{ animationDuration: `${motion.base}ms` }}
        >
          <span className="toast__msg">{toast.message}</span>
          {toast.onUndo ? (
            <button
              type="button"
              className="toast__undo"
              onClick={() => {
                if (used.current === toast.id) return;
                used.current = toast.id;
                toast.onUndo?.();
                onDone();
              }}
            >
              {toast.undoLabel ?? t('user.saved.undo')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
